import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type TableColumnFilterOption, type TableFilterOption } from '@avanta/shared/types/table';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageHeader } from 'components/PageHeader';
import { ROUTES } from 'constants/routes';
import { type AppointmentWithDetails } from 'types/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { TableActions } from './TableActions';
import { useFetchEnterpriseAppointments } from './useFetchEnterpriseAppointments';
import { useSessionsSummary } from './useSessionsSummary';
import { getSessionStatus, SESSION_STATUS, type SessionStatus } from './utils';

const getSpecialityLabel = (specialityId: number): string =>
  t(`AppointmentPdf.Speciality.options.${specialityId}`, {
    ns: LOCIZE_NS.APPOINTMENTS,
  });

const getSpecialityFilterOptions = (
  appointments: AppointmentWithDetails[]
): TableColumnFilterOption[] => {
  const specialityIds = new Set<number>();

  for (const appointment of appointments) {
    specialityIds.add(appointment.specialitiesBookable);
  }

  return [...specialityIds]
    .toSorted((a, b) => a - b)
    .map(specialityId => ({
      value: specialityId,
      label: getSpecialityLabel(specialityId),
    }));
};

const getFilterOptions = (): TableFilterOption<SessionStatus>[] => [
  {
    key: SESSION_STATUS.CONFIRMED,
    label: t('SessionsPage.filters.confirmed', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: SESSION_STATUS.PENDING,
    label: t('SessionsPage.filters.pending', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: SESSION_STATUS.CANCELED,
    label: t('SessionsPage.filters.canceled', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: SESSION_STATUS.COMPLETED,
    label: t('SessionsPage.filters.completed', { ns: LOCIZE_NS.INSURANCE }),
  },
];

const matchesSessionFilter = (row: AppointmentWithDetails, selectedKey: SessionStatus): boolean =>
  getSessionStatus(row) === selectedKey;

const getTableColumns = (
  specialityFilterOptions: TableColumnFilterOption[]
): ColumnDef<AppointmentWithDetails>[] => [
  {
    id: 'dateTime',
    header: t('SessionsPage.Table.date.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => row.dateTime,
    enableSorting: true,
    cell: ({ row }): string => formatUnifiedForDisplay(row.original.dateTime),
  },
  {
    id: 'specialitiesBookable',
    header: t('SessionsPage.Table.speciality.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => getSpecialityLabel(row.specialitiesBookable),
    meta: {
      columnFilter: {
        options: specialityFilterOptions,
      },
    },
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'psychologist',
    header: t('SessionsPage.Table.psychologist.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => `${row.psychologist?.name ?? ''} ${row.psychologist?.surname ?? ''}`.trim(),
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'center',
    header: t('SessionsPage.Table.center.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'patient',
    header: t('SessionsPage.Table.patient.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => `${row.patient?.firstName ?? ''} ${row.patient?.surname ?? ''}`.trim(),
    enableSorting: true,
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'status',
    header: t('SessionsPage.Table.status.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row =>
      t(`SessionsPage.filters.${getSessionStatus(row)}`, { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'actions',
    header: t('SessionsPage.Table.actions.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): ReactElement => <TableActions appointment={row.original} />,
  },
];

export const Appointments = (): ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const { data: appointments = [], isLoading } = useFetchEnterpriseAppointments();
  const stats = useSessionsSummary(appointments);

  const columns = useMemo(
    () => getTableColumns(getSpecialityFilterOptions(appointments)),
    [appointments]
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('InsuranceAppointmentsList.heading')}
        description={t('SessionsPage.description', {
          total: stats.total,
          confirmed: stats.confirmed,
          pending: stats.pending,
          today: stats.today,
        })}
      >
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() =>
            navigate(ROUTES.APPOINTMENTS_RESERVE, { state: { flow: FLOW_MODES.search_patient } })
          }
        >
          {t('SessionsPage.scheduleCta')}
        </Button>
      </PageHeader>
      <ClientPaginatedTable<AppointmentWithDetails, SessionStatus>
        data={appointments}
        columns={columns}
        isLoading={isLoading}
        emptyTitle={t('SessionsPage.empty')}
        tableClassName="w-full"
        withSearch
        searchPlaceholder="SessionsPage.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        filter={{
          accessorKey: 'confirmed',
          options: getFilterOptions(),
          filterFn: matchesSessionFilter,
        }}
      />
    </div>
  );
};
